import { Context } from '../context/Context';
import { ShaderSourceParser } from './shader-source-parser';
import { CubeMapTexture, Texture } from '../texture';

export type ShaderAttr = { [key: string]: number };
export type ShaderUniforms = {
  [key: string]: WebGLUniformLocation | WebGLUniformLocation[];
};

type ShaderSettings = {
  shader: WebGLShader;
  uniforms: {
    name: string;
    dataType: string;
    isArray: boolean;
    arraySize?: number;
  }[];
  attributes: {
    name: string;
    dataType: string;
    location?: number;
  }[];
};

export interface Shader {
  program: WebGLProgram;

  enable(): void;

  disable(): void;

  next(): void;
}

export abstract class ShaderBase<
  TUniforms extends ShaderUniforms,
  TAttributes extends ShaderAttr,
> implements Shader {
  public program: WebGLProgram;

  protected get warnAttributeMismatch(): boolean {
    return true;
  }

  public get attributes(): TAttributes {
    return this._attributes as TAttributes;
  }

  public get uniforms(): TUniforms {
    return this._uniforms as TUniforms;
  }

  public abstract get version(): string;

  public abstract get parser(): ShaderSourceParser;

  private _attributes: ShaderAttr;
  private _uniforms: ShaderUniforms;

  constructor(
    public ctx: Context,
    fragmentShaderSource: string,
    vertexShaderSource: string,
    settings: string[],
  ) {
    this._attributes = {};
    this._uniforms = {};
    this.program = this.initShaders(fragmentShaderSource, vertexShaderSource, settings);
  }

  public get gl(): WebGL2RenderingContext {
    return this.ctx.gl;
  }

  public abstract get uniformsContainer(): ShaderUniformsContainer;

  public abstract get attributesContainer(): ShaderAttributesContainer;

  public enable() {
    this.uniformsContainer.activate();
    this.attributesContainer.activate();
  }

  public disable() {
    this.uniformsContainer.clear();
    this.attributesContainer.clear();
  }

  public validate() {
    this.uniformsContainer.validate();
    this.attributesContainer.validate();
  }

  public next() {
    this.uniformsContainer.next();
    this.attributesContainer.next();
  }

  protected initShaders(
    fragmentShaderSource: string,
    vertexShaderSource: string,
    settings: string[],
  ): WebGLProgram {
    let gl = this.gl;

    let fragmentShader = this.compileShader(fragmentShaderSource, gl.FRAGMENT_SHADER, settings);
    let vertexShader = this.compileShader(vertexShaderSource, gl.VERTEX_SHADER, settings);

    let shaderProgram = gl.createProgram();
    gl.attachShader(shaderProgram, vertexShader.shader);
    gl.attachShader(shaderProgram, fragmentShader.shader);
    gl.linkProgram(shaderProgram);

    if (!gl.getProgramParameter(shaderProgram, gl.LINK_STATUS)) {
      console.error('Could not initialise shaders', gl.getProgramInfoLog(shaderProgram));
    }

    this.postLinkShader(shaderProgram, vertexShader);
    this.postLinkShader(shaderProgram, fragmentShader);

    // Shader objects are no longer needed after linking — the program retains the compiled code
    gl.detachShader(shaderProgram, vertexShader.shader);
    gl.deleteShader(vertexShader.shader);
    gl.detachShader(shaderProgram, fragmentShader.shader);
    gl.deleteShader(fragmentShader.shader);

    return shaderProgram;
  }

  private postLinkShader(shaderProgram: WebGLProgram, shader: ShaderSettings) {
    let gl = this.gl;

    for (let attr of shader.attributes) {
      if (attr.location !== undefined) {
        this._attributes[attr.name] = attr.location;
      } else {
        let attrLocation = gl.getAttribLocation(shaderProgram, attr.name);
        if (attrLocation != -1) {
          this._attributes[attr.name] = attrLocation;
        } else {
          if (this.warnAttributeMismatch) {
            console.warn(
              `Shader attribute ${attr.name} is not found in shader or is unused in shader.`,
            );
          }
        }
      }
    }

    for (let uniform of shader.uniforms) {
      if (!uniform.isArray) {
        this._uniforms[uniform.name] = gl.getUniformLocation(shaderProgram, uniform.name)!;
      } else {
        let name = uniform.name;
        let size = uniform.arraySize ?? 0;

        let arr = new Array<WebGLUniformLocation>();
        for (let j = 0; j < size; j++) {
          arr.push(gl.getUniformLocation(shaderProgram, name + '[' + j + ']')!);
        }
        this._uniforms[name] = arr;
      }
    }
  }

  protected compileShader(source: string, shaderType: number, settings: string[]): ShaderSettings {
    let gl = this.gl;
    let shader = gl.createShader(shaderType);

    if (!shader) {
      throw new Error('Could not create shader of type ' + shaderType);
    }

    let result = this.parser.parse(source);

    let versionHeader = this.version ? `#version ${this.version}\n` : '';

    let header = `${versionHeader}
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
precision highp int;
#else
precision mediump float;
precision mediump int;
#endif
${settings.map((setting) => `#define NOODLE_${setting.toUpperCase()}\n`).join('')}`;

    gl.shaderSource(shader, header + source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.log(header + source);
      console.error(gl.getShaderInfoLog(shader));
      console.log(this.parser.preprocess(source, settings));

      throw new Error('An error occurred compiling the shaders: ' + gl.getShaderInfoLog(shader));
    }

    return {
      shader: shader,
      uniforms: result.uniforms,
      attributes: result.attributes,
    };
  }
}

export enum AttributeDataType {
  float = 'float',
  vec2 = 'vec2',
  vec3 = 'vec3',
  vec4 = 'vec4',
  mat3 = 'mat3',
  mat4 = 'mat4',
  uvec2 = 'uvec2',
  uvec3 = 'uvec3',
  uvec4 = 'uvec4',
}

export enum UniformDataType {
  bool = 'bool',
  int = 'int',
  float = 'float',
  vec2 = 'vec2',
  vec3 = 'vec3',
  vec4 = 'vec4',
  mat3 = 'mat3',
  mat4 = 'mat4',
  texture2d = 'sampler2D',
  textureCube = 'samplerCube',
  texture2dArray = 'sampler2DArray',
}

export abstract class ShaderUniformsContainer {
  public debug: boolean;

  private nextTexture: number = 0;
  private warnSet: Set<string>;
  private dataMap: Map<WebGLUniformLocation, any>;
  private static maxTextureUnits: number = 0;

  private allocateTextureSlot(uniformName: string): number {
    if (ShaderUniformsContainer.maxTextureUnits === 0) {
      ShaderUniformsContainer.maxTextureUnits = this.gl.getParameter(
        this.gl.MAX_TEXTURE_IMAGE_UNITS,
      );
    }
    if (this.nextTexture >= ShaderUniformsContainer.maxTextureUnits) {
      throw new Error(
        `Texture units exhausted (${this.nextTexture}/${ShaderUniformsContainer.maxTextureUnits}) ` +
          `while binding sampler '${uniformName}' on ${this.constructor.name}`,
      );
    }
    return this.nextTexture++;
  }

  protected get gl(): WebGL2RenderingContext {
    return this.ctx.gl;
  }

  constructor(protected ctx: Context) {
    this.dataMap = new Map();
    this.warnSet = new Set();
    this.debug = false;
  }

  public next(): void {
    // for (let i = 0; i < this.nextTexture; i++) {
    //     this.ctx.textureState.clearTexture(i);
    // }

    this.nextTexture = 0;
  }

  public clear(): void {
    this.dataMap.clear();
  }

  public activate(): void {}

  protected abstract getTextureUniforms(): {
    name: string;
    uniform: WebGLUniformLocation;
    dataType: UniformDataType | string;
  }[];

  public validate(): void {
    let uniforms = this.getTextureUniforms();

    for (let textureUni of uniforms) {
      if (this.debug) {
        console.log(
          `[${textureUni.uniform}] ${textureUni.name} => ${textureUni.dataType}: ${this.getValue(textureUni.uniform)}`,
        );
      }

      if (textureUni.uniform === null) {
        continue; // texture not used in this version of shader
      }

      let location = this.getValue(textureUni.uniform);
      let textureSlot: number = location === false ? 0 : location;

      let texture = this.ctx.textureState.getTexture(textureSlot);
      if (texture) {
        if (textureUni.dataType == UniformDataType.texture2d && !texture.is2d) {
          this.setValue(
            textureUni.name,
            textureUni.uniform,
            this.fetchTexturePosition(this.ctx.textureState.placeHolder2d),
            textureUni.dataType,
          );
        } else if (textureUni.dataType == UniformDataType.textureCube && !texture.isCube) {
          this.setValue(
            textureUni.name,
            textureUni.uniform,
            this.fetchTexturePosition(this.ctx.textureState.placeHolderCubeMap),
            textureUni.dataType,
          );
        }
      } else {
      }
    }
  }

  protected fetchTexturePosition(texture: Texture): number {
    let pos = this.ctx.textureState.getTexturePosition(texture);
    if (pos === undefined) {
      pos = this.allocateTextureSlot('<placeholder>');
      this.ctx.textureState.setTexture(pos, texture);
    }

    return pos as number;
  }

  protected setTextureValue(
    uniformName: string,
    uniformLocation: WebGLUniformLocation,
    texture: Texture,
    dataType: UniformDataType | string,
  ): boolean {
    const textureSlot = this.allocateTextureSlot(uniformName);

    if (texture instanceof CubeMapTexture) {
      if (dataType != UniformDataType.textureCube) {
        throw new Error(`Attempted to bind a cube texture to ${uniformName} (${dataType})`);
      }
    } else if (texture.is2dArray) {
      if (dataType != UniformDataType.texture2dArray) {
        throw new Error(`Attempted to bind a 2D-array texture to ${uniformName} (${dataType})`);
      }
    } else {
      if (dataType != UniformDataType.texture2d) {
        throw new Error(`Attempted to bind a 2d texture to ${uniformName} (${dataType})`);
      }
    }

    this.ctx.textureState.setTexture(textureSlot, texture);

    return this.setValue(uniformName, uniformLocation, textureSlot, dataType);
  }

  protected getValue(uniformLocation: WebGLUniformLocation): any {
    if (this.dataMap.has(uniformLocation)) {
      return this.dataMap.get(uniformLocation);
    } else {
      return false;
    }
  }

  protected setValue(
    uniformName: string,
    uniformLocation: WebGLUniformLocation,
    value: any,
    dataType: UniformDataType | string,
  ): boolean {
    if (uniformLocation === null) {
      if (!this.warnSet.has(uniformName)) {
        console.warn(
          `Attempting to set value to uniform ${uniformName} (${dataType}), but this uniform is not used in the shader`,
        );
        //console.trace();
        this.warnSet.add(uniformName);
      }

      // When uniform is unused in shader it may get optimized away resulting in null. In such a case don't
      // bother sending data to GL API as it is discarded anyway.
      return false;
    }

    if (value === null || value === undefined) {
      throw new Error(`Uniform can not be set to null`);
    }

    let set =
      !this.dataMap.has(uniformLocation) ||
      !this.dataMatch(dataType, this.dataMap.get(uniformLocation), value);
    if (set) {
      switch (dataType) {
        case UniformDataType.bool:
          this.gl.uniform1i(uniformLocation, value ? 1 : 0);
          break;
        case UniformDataType.int:
        case UniformDataType.texture2d:
        case UniformDataType.textureCube:
        case UniformDataType.texture2dArray:
          this.gl.uniform1i(uniformLocation, value);
          break;
        case UniformDataType.float:
          this.gl.uniform1f(uniformLocation, value);
          break;
        case UniformDataType.vec2:
          this.gl.uniform2fv(uniformLocation, value);
          break;
        case UniformDataType.vec3:
          this.gl.uniform3fv(uniformLocation, value);
          break;
        case UniformDataType.vec4:
          this.gl.uniform4fv(uniformLocation, value);
          break;
        case UniformDataType.mat3:
          this.gl.uniformMatrix3fv(uniformLocation, false, value);
          break;
        case UniformDataType.mat4:
          this.gl.uniformMatrix4fv(uniformLocation, false, value);
          break;
      }

      // Store a snapshot so dirty-checking works correctly when the caller
      // reuses a mutable buffer (e.g. a Float32Array mutated in-place each frame).
      this.dataMap.set(
        uniformLocation,
        ArrayBuffer.isView(value)
          ? (value as Float32Array).slice()
          : Array.isArray(value)
            ? value.slice()
            : value,
      );
    }

    return set;
  }

  protected dataMatch(
    dataType: UniformDataType | string,
    originalValue: any,
    newValue: any,
  ): boolean {
    switch (dataType) {
      case UniformDataType.bool:
      case UniformDataType.float:
      case UniformDataType.int:
      case UniformDataType.texture2d:
      case UniformDataType.textureCube:
      case UniformDataType.texture2dArray:
        return originalValue === newValue;
      case UniformDataType.vec2:
        return this.matchArray(originalValue, newValue, 2);
      case UniformDataType.vec3:
        return this.matchArray(originalValue, newValue, 3);
      case UniformDataType.vec4:
        return this.matchArray(originalValue, newValue, 4);
      case UniformDataType.mat3:
        return this.matchArray(originalValue, newValue, 9);
      case UniformDataType.mat4:
        return this.matchArray(originalValue, newValue, 16);
    }

    return false;
  }

  protected matchArray(a: number[], b: number[], elements: number): boolean {
    for (let i = 0; i < elements; i++) {
      if (a[i] !== b[i]) {
        return false;
      }
    }
    return true;
  }
}

export class ShaderAttributesContainer {
  private activeAttributes: Set<number>;

  protected get gl(): WebGL2RenderingContext {
    return this.ctx.gl;
  }

  constructor(protected ctx: Context) {
    this.activeAttributes = new Set();
  }

  public clear(): void {
    // With VAOs, attribute enable/disable state is encapsulated in the VAO.
    // No need to disable individual vertex attrib arrays.
  }

  public activate(): void {}

  public validate(): void {}

  public next(): void {}

  protected bindFloatAttribute(
    attribute: number,
    length: number,
    offset: number,
    itemSize: number,
    glType: number,
    elementSize: number,
  ): boolean {
    const gl = this.gl;
    if (!this.activeAttributes.has(attribute)) {
      gl.enableVertexAttribArray(attribute);
      this.activeAttributes.add(attribute);
    }
    gl.vertexAttribPointer(
      attribute,
      length,
      glType,
      false,
      elementSize * itemSize,
      elementSize * offset,
    );
    return true;
  }

  protected bindIntAttribute(
    attribute: number,
    length: number,
    offset: number,
    itemSize: number,
    glType: number,
    elementSize: number,
  ): boolean {
    const gl = this.gl;
    if (!this.activeAttributes.has(attribute)) {
      gl.enableVertexAttribArray(attribute);
      this.activeAttributes.add(attribute);
    }
    gl.vertexAttribIPointer(
      attribute,
      length,
      glType,
      elementSize * itemSize,
      elementSize * offset,
    );
    return true;
  }
}
